/**
 * Phase offset — local-only correction for the bar.beat mismatch between
 * Live's transport and Link's continuous beat counter.
 *
 * Why: Link shares tempo + beat-rate between peers but NOT a bar origin.
 * Link's `beat = 0` is an arbitrary timeline moment, not Live's `1.1.1`.
 * So our "Beat N/4" display is offset from Live's by whatever beat the
 * session started on, and that offset is constant for the duration of the
 * Link session.
 *
 * Workaround until we have Live's real transport (via Max-for-Live OSC):
 * user taps an "on 1" button on Live's downbeat, we capture the current
 * raw phase, store its negative as the offset. From then on, the displayed
 * phase = (rawPhase + offset) mod 1.
 *
 * Persisted in localStorage so it survives reloads — Live's session
 * remembers where its transport started, the offset stays valid until you
 * restart Live or change time-signature.
 */

import { createSignal, type Accessor } from 'solid-js';

const STORAGE_KEY = 'opbuttons.phaseOffset';

function loadOffset(): number {
  if (typeof localStorage === 'undefined') return 0;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? ((n % 1) + 1) % 1 : 0;
}

function saveOffset(v: number): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, String(v));
}

const [offset, setOffsetSignal] = createSignal(loadOffset());

export const phaseOffset: Accessor<number> = offset;

/**
 * Set a new offset directly. `v` is normalized to 0..1. Use this from a
 * "calibrate" UI; for the common case use `tapOnDownbeat` instead.
 */
export function setPhaseOffset(v: number): void {
  const norm = ((v % 1) + 1) % 1;
  setOffsetSignal(norm);
  saveOffset(norm);
}

/**
 * Capture the current raw phase as Live's downbeat. From this moment on,
 * (rawPhase + offset) % 1 will land on 0 at the moment of the next downbeat
 * the user taps. In other words: tap exactly on Live's beat 1.
 */
export function tapOnDownbeat(rawPhase: number): void {
  // We want display(rawPhase) == 0 at this instant, so offset = -rawPhase.
  setPhaseOffset(1 - rawPhase);
}

/**
 * Apply the offset to a raw phase (0..1) and wrap. Plain function so
 * callers can use it without a reactive subscription if they don't need one.
 */
export function applyPhaseOffset(rawPhase: number, off: number): number {
  return ((rawPhase + off) % 1 + 1) % 1;
}
